import React, { useState } from 'react';
import { Building2, Search, Award, TrendingUp, Lock, Sparkles, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  extractDegreePrefix,
  calculateScore,
  SEARCH_CONFIG,
  getPrefixVariant
} from '../utils/rncpSearchUtils';

/**
 * Page de test de fusion de compétences
 * Permet d'analyser les formations d'un organisme et trouver les opportunités de fusion
 */
const FusionTest = () => {
  const navigate = useNavigate();

  // États
  const [siret, setSiret] = useState('');
  const [loading, setLoading] = useState(false);
  const [organism, setOrganism] = useState(null);
  const [formations, setFormations] = useState([]);
  const [fusionAnalysis, setFusionAnalysis] = useState(null);
  const [showAllFormations, setShowAllFormations] = useState(false);
  const [fusionThreshold, setFusionThreshold] = useState(50); // Seuil de fusion par défaut 50%

  // Fonction pour formater le SIRET avec espaces (EXACTEMENT comme IntercariforefTest)
  const formatSiret = (value) => {
    const numbers = value.replace(/\s/g, '');
    if (numbers.length <= 14) {
      return numbers.replace(/(\d{3})(\d{3})?(\d{3})?(\d{2})?(\d{3})?/, (match, p1, p2, p3, p4, p5) => {
        let result = p1;
        if (p2) result += ' ' + p2;
        if (p3) result += ' ' + p3;
        if (p4) result += ' ' + p4;
        if (p5) result += ' ' + p5;
        return result;
      });
    }
    return value;
  };

  // Étape 1: Rechercher l'organisme par SIRET (EXACTEMENT comme IntercariforefTest)
  const handleSearchSiret = async () => {
    const cleanSiret = siret.replace(/\s/g, '');

    if (!cleanSiret) {
      toast.error('Veuillez entrer un SIRET');
      return;
    }

    if (cleanSiret.length !== 14 || !/^\d{14}$/.test(cleanSiret)) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }

    setLoading(true);
    setOrganism(null);
    setFormations([]);

    try {
      // Utiliser l'API backend /intercariforef/scrape (même endpoint qu'IntercariforefTest)
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/scrape`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ siret: cleanSiret })
        }
      );

      const data = await response.json();
      if (data.success && data.data) {
        setOrganism({
          name: data.data.organism?.name || 'Organisme trouvé',
          url: data.data.organismUrl,
          siret: data.data.siret,
          address: data.data.organism?.address,
          totalFormations: data.data.totalFormations
        });
        toast.success(`Organisme trouvé: ${data.data.organism?.name || 'Organisme'}`);
      } else {
        toast.error(data.error || 'Organisme non trouvé pour ce SIRET');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
      console.error('❌ Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // Étape 2: Valider l'organisme et charger les formations (EXACTEMENT comme IntercariforefTest)
  const handleValidateOrganism = async () => {
    if (!organism) return;

    const cleanSiret = siret.replace(/\s/g, '');

    setLoading(true);
    setFormations([]);

    try {
      // Utiliser l'API backend /intercariforef/scrape (même endpoint qu'IntercariforefTest)
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/scrape`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ siret: cleanSiret })
        }
      );

      const data = await response.json();

      if (data.success && data.data?.formations) {
        const baseFormations = data.data.formations;

        // Dédupliquer les formations par titre (comme IntercariforefTest)
        const formationsMap = new Map();

        baseFormations.forEach(formation => {
          const normalizedTitle = formation.title.trim().toLowerCase();

          if (formationsMap.has(normalizedTitle)) {
            // Formation déjà présente, ajouter le lieu à la liste
            const existing = formationsMap.get(normalizedTitle);
            if (formation.location && !existing.locations.includes(formation.location)) {
              existing.locations.push(formation.location);
            }
          } else {
            // Nouvelle formation, créer l'entrée
            formationsMap.set(normalizedTitle, {
              ...formation,
              locations: formation.location ? [formation.location] : [],
              originalLocation: formation.location
            });
          }
        });

        // Convertir en tableau de formations uniques
        const uniqueFormations = Array.from(formationsMap.values());

        toast.success(`${baseFormations.length} formation(s) récupérée(s) → ${uniqueFormations.length} formation(s) unique(s)`);

        console.log(`📚 ${uniqueFormations.length} formations uniques trouvées`);

        // Récupérer les compétences pour chaque formation
        await loadCompetencesForFormations(uniqueFormations);
      } else {
        toast.error('Erreur lors du chargement des formations');
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des formations');
      console.error('❌ Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction d'enrichissement RNCP (utilise scraping comme Onboarding)
  const enrichFormationWithRncp = async (formation, index) => {
    try {
      // Délai de 2 secondes entre chaque requête pour ne pas surcharger le scraping
      if (index > 0) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      const apiSearchTerm = formation.title;
      console.log(`\n🔍 Recherche RNCP pour: "${apiSearchTerm}"`);

      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      // Étape 1: Rechercher dans la BDD locale (comme Onboarding)
      console.log(`🔍 Recherche RNCP dans BDD locale...`);
      const searchUrl = `${import.meta.env.VITE_API_URL}/rncp/smart-search?q=${encodeURIComponent(apiSearchTerm)}&limit=10`;

      const searchResponse = await fetch(searchUrl, { headers });
      const searchData = await searchResponse.json();

      console.log(`📦 Résultats recherche BDD:`, {
        success: searchData.success,
        resultCount: searchData.data?.certifications?.length || 0
      });

      // Si aucun résultat trouvé
      if (!searchData.success || !searchData.data?.certifications || searchData.data.certifications.length === 0) {
        console.log(`⚠️ Aucun RNCP trouvé pour: ${apiSearchTerm}`);
        return {
          ...formation,
          rncpCode: null,
          competences: [],
          blocs: [],
          competencesMetier: [],
          totalCompetences: 0,
          relevanceScore: 0
        };
      }

      // Prendre le meilleur match
      const bestMatch = searchData.data.certifications[0];
      const rncpCode = bestMatch.rncpCode;

      console.log(`✅ RNCP trouvé:`, {
        rncpCode,
        title: bestMatch.title,
        score: bestMatch.relevanceScore
      });

      // Nettoyer le code RNCP
      const cleanRncpCode = rncpCode.replace(/^RNCP/i, '');

      // Étape 2: Synchroniser (scraper) la certification
      console.log(`🔄 Synchronisation de RNCP${cleanRncpCode}...`);

      const syncResponse = await fetch(`${import.meta.env.VITE_API_URL}/rncp/sync`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          rncpCodes: [cleanRncpCode]
        })
      });

      const syncData = await syncResponse.json();

      if (syncResponse.ok && syncData.success) {
        console.log(`✅ RNCP${cleanRncpCode} synchronisé:`, {
          certifications: syncData.data?.details?.certifications,
          blocks: syncData.data?.details?.blocks,
          competences: syncData.data?.details?.competences
        });

        // Délai pour laisser la BDD se mettre à jour
        await new Promise(resolve => setTimeout(resolve, 2000));
      } else {
        console.warn(`⚠️ Synchronisation échouée pour RNCP${cleanRncpCode}`);
      }

      // Étape 3: Récupérer les détails depuis la BDD locale
      console.log(`📥 Récupération détails depuis BDD...`);
      const detailsUrl = `${import.meta.env.VITE_API_URL}/rncp/${cleanRncpCode}/details`;

      const detailsResponse = await fetch(detailsUrl, { headers });
      const detailsData = await detailsResponse.json();

      console.log(`📦 Détails RNCP:`, {
        success: detailsData.success,
        blocsCount: detailsData.blocks?.length || 0,
        competencesCount: detailsData.competences?.length || 0
      });

      if (detailsData.success && detailsData.competences) {
        // Extraire les intitulés des compétences
        const competences = detailsData.competences.map(c => c.intitule || c.title || c.description);

        return {
          ...formation,
          rncpCode: rncpCode,
          competences: competences,
          blocs: detailsData.blocks || [],
          competencesMetier: [], // Pas utilisé avec scraping
          codesRome: [],
          totalBlocs: detailsData.blocks?.length || 0,
          totalCompetences: competences.length,
          relevanceScore: bestMatch.relevanceScore || 100
        };
      }

      return {
        ...formation,
        rncpCode: null,
        rncpData: null,
        blocs: [],
        totalBlocs: 0,
        totalCompetences: 0,
        relevanceScore: 0
      };
    } catch (error) {
      console.error(`Erreur enrichissement:`, error);
      return {
        ...formation,
        rncpCode: null,
        blocs: [],
        totalBlocs: 0,
        totalCompetences: 0
      };
    }
  };

  // Étape 3: Charger les compétences RNCP pour chaque formation (EXACTEMENT comme IntercariforefTest)
  const loadCompetencesForFormations = async (formationsData) => {
    const formationsWithCompetences = [];

    for (let i = 0; i < formationsData.length; i++) {
      console.log(`\n[${i + 1}/${formationsData.length}] Enrichissement en cours...`);
      const enriched = await enrichFormationWithRncp(formationsData[i], i);

      // 📊 LOG DÉTAILLÉ: Afficher ce qui a été trouvé
      console.log(`   📋 Code RNCP trouvé: ${enriched.rncpCode || 'AUCUN'}`);
      console.log(`   📦 Blocs RNCP: ${enriched.blocs?.length || 0}`);
      console.log(`   🎯 Total compétences: ${enriched.totalCompetences || 0}`);
      console.log(`   📊 Score de pertinence: ${enriched.relevanceScore || 0}%`);

      // Si un RNCP a été trouvé, afficher les détails des blocs
      if (enriched.rncpCode && enriched.blocs && enriched.blocs.length > 0) {
        console.log(`   📝 Détail des blocs:`);
        enriched.blocs.forEach((bloc, idx) => {
          console.log(`      [${idx + 1}] ${bloc.code} - ${bloc.libelle} (${bloc.competences?.length || 0} compétences)`);
        });
      }

      // Ne garder que les formations avec RNCP trouvé ET compétences scrapées
      if (enriched.rncpCode && enriched.competences && enriched.competences.length > 0) {
        formationsWithCompetences.push(enriched);
        console.log(`   ✅ Formation gardée (${enriched.competences.length} compétences scrapées)`);
      } else {
        console.log(`   ⚠️ Formation exclue - Raisons:`);
        if (!enriched.rncpCode) {
          console.log(`      - Pas de code RNCP trouvé`);
        } else {
          console.log(`      - Code RNCP trouvé: ${enriched.rncpCode}`);
          if (!enriched.competences || enriched.competences.length === 0) {
            console.log(`      - Mais aucune compétence détaillée scrapée`);
          }
        }
      }
    }

    console.log(`\n📊 FILTRAGE: ${formationsWithCompetences.length}/${formationsData.length} formations gardées`);
    console.log(`✅ ${formationsWithCompetences.length} formations avec compétences RNCP`);

    // 📊 DEBUG: Afficher les formations avec leurs compétences
    console.log('\n📋 FORMATIONS AVEC COMPÉTENCES RNCP:');
    formationsWithCompetences.forEach((formation, index) => {
      console.log(`\n[${index + 1}] ${formation.title}`);
      console.log(`   RNCP: ${formation.rncpCode}`);
      console.log(`   Blocs RNCP: ${formation.totalBlocs}`);
      console.log(`   Compétences RNCP totales: ${formation.totalCompetences}`);
      console.log(`   Détail des compétences par bloc:`);
      formation.blocs?.forEach((bloc, blocIndex) => {
        const competencesCount = bloc.competences?.length || 0;
        console.log(`     [Bloc ${blocIndex + 1}] ${bloc.libelle} (${bloc.code}) - ${competencesCount} compétences`);
        if (bloc.competences && bloc.competences.length > 0) {
          const firstTwo = bloc.competences.slice(0, 2).map(c => typeof c === 'string' ? c : (c.libelle || 'N/A'));
          console.log(`       ${firstTwo.join('\n       ')}`);
        }
      });
    });

    setFormations(formationsWithCompetences);

    // Lancer l'analyse de fusion
    if (formationsWithCompetences.length > 0) {
      analyzeFormationsFusion(formationsWithCompetences, fusionThreshold);
    } else {
      toast.error('Aucune formation avec code RNCP trouvée');
    }
  };

  // Étape 4: Analyser les fusions possibles entre formations
  const analyzeFormationsFusion = (formationsData, threshold = 50) => {
    console.log('\n🔬 DÉBUT ANALYSE DE FUSION');
    console.log(`   ${formationsData.length} formations à analyser`);
    console.log(`   Seuil de fusion: ${threshold}%`);

    const fusionPairs = [];
    const allComparisons = [];

    // Comparer chaque paire de formations
    for (let i = 0; i < formationsData.length; i++) {
      for (let j = i + 1; j < formationsData.length; j++) {
        const formation1 = formationsData[i];
        const formation2 = formationsData[j];

        const fusionResult = calculateFormationFusion(formation1, formation2);

        allComparisons.push({
          pair: `${i+1}-${j+1}`,
          f1: formation1.title?.substring(0, 50),
          f2: formation2.title?.substring(0, 50),
          merged: fusionResult.mergedCompetences.length,
          similarity: fusionResult.similarity,
          comp1: fusionResult.totalCompetences1,
          comp2: fusionResult.totalCompetences2,
          sameRncp: fusionResult.sameRncp
        });

        if (fusionResult.similarity >= threshold) {
          console.log(`   ✅ Paire fusion trouvée: [${i+1}] x [${j+1}] - ${fusionResult.mergedCompetences.length} compétences fusionnées (${fusionResult.similarity}%)`);
          console.log(`      Formation 1: ${fusionResult.totalCompetences1} compétences`);
          console.log(`      Formation 2: ${fusionResult.totalCompetences2} compétences`);
          console.log(`      Union: ${fusionResult.totalCompetences1 + fusionResult.totalCompetences2 - fusionResult.mergedCompetences.length}`);
          fusionPairs.push({
            formation1,
            formation2,
            ...fusionResult
          });
        } else if (fusionResult.sameRncp) {
          console.log(`   ⚠️  Paire [${i+1}] x [${j+1}] ignorée: même RNCP (${formation1.rncpCode})`);
        }
      }
    }

    // Afficher toutes les comparaisons pour debug (avec détails du calcul)
    console.log('\n📊 TOUTES LES COMPARAISONS (Top 10):');
    allComparisons
      .sort((a, b) => b.merged - a.merged)
      .slice(0, 10)
      .forEach(comp => {
        if (comp.sameRncp) {
          console.log(`   [${comp.pair}] ⚠️ IGNORÉ (même RNCP) - ${comp.f1} x ${comp.f2}`);
        } else {
          const union = comp.comp1 + comp.comp2 - comp.merged;
          console.log(`   [${comp.pair}] ${comp.merged} fusionnées (${comp.similarity}%) = ${comp.merged}/${union} - ${comp.f1} x ${comp.f2}`);
          console.log(`           Détail: ${comp.comp1} + ${comp.comp2} - ${comp.merged} = ${union} compétences union`);
        }
      });

    // Trier par nombre de compétences fusionnées (décroissant)
    fusionPairs.sort((a, b) => b.mergedCompetences.length - a.mergedCompetences.length);

    console.log(`\n🔍 ${fusionPairs.length} paires de fusion trouvées (seuil ${threshold}%)`);

    if (fusionPairs.length > 0) {
      setFusionAnalysis({
        topPair: fusionPairs[0],
        allPairs: fusionPairs,
        totalFormations: formationsData.length
      });
      toast.success(`${fusionPairs.length} opportunités de fusion trouvées (seuil: ${threshold}%)!`);
    } else {
      toast.error(`Aucune opportunité de fusion trouvée (seuil: ${threshold}%)`);
    }
  };

  // Calculer la similarité et fusion entre 2 formations (utilise scraping comme Onboarding)
  const calculateFormationFusion = (formation1, formation2) => {
    // Utiliser directement les competences scrapées depuis France Compétences
    const competences1 = formation1.competences || [];
    const competences2 = formation2.competences || [];

    // ❌ EXCLUSION: Ne pas comparer si même certification RNCP
    if (formation1.rncpCode === formation2.rncpCode) {
      return {
        similarity: 0,
        mergedCompetences: [],
        hoursSaved: 0,
        totalCompetences1: competences1.length,
        totalCompetences2: competences2.length,
        sameRncp: true
      };
    }

    const mergedCompetences = [];
    const processed = new Set(); // Pour éviter de compter plusieurs fois la même compétence

    // Algorithme de fusion (compétences sont des strings simples)
    competences1.forEach((comp1Text, index1) => {
      if (processed.has(`1_${index1}`)) return;

      const similar = [{ text: comp1Text, formation: 1 }];
      const similarIndices = [`1_${index1}`];

      // Comparer avec toutes les compétences de formation2
      competences2.forEach((comp2Text, index2) => {
        if (processed.has(`2_${index2}`)) return;

        const similarity = calculateTextSimilarity(comp1Text, comp2Text);

        // Seuil fixe de 80% pour considérer deux compétences comme similaires
        if (similarity >= 80) {
          similar.push({ text: comp2Text, formation: 2 });
          similarIndices.push(`2_${index2}`);
        }
      });

      // Marquer comme traité
      similarIndices.forEach(idx => processed.add(idx));

      // Si au moins une correspondance trouvée
      if (similar.length > 1) {
        const comp2Match = similar.find(s => s.formation === 2);
        mergedCompetences.push({
          competence1: { text: comp1Text, rncpCode: formation1.rncpCode },
          competence2: comp2Match ? { text: comp2Match.text, rncpCode: formation2.rncpCode } : null,
          similarity: comp2Match ? calculateTextSimilarity(comp1Text, comp2Match.text) : 0,
          count: similar.length
        });
      }
    });

    // ✅ CORRECTION DU CALCUL: Similarité = (compétences fusionnées / union des compétences) * 100
    // Comme Jaccard: intersection / union
    const unionSize = competences1.length + competences2.length - mergedCompetences.length;
    const similarityPercentage = unionSize > 0
      ? Math.round((mergedCompetences.length / unionSize) * 100)
      : 0;

    // Calculer le temps économisé (estimation: 2h par compétence fusionnée)
    const hoursSaved = mergedCompetences.length * 2;

    return {
      similarity: similarityPercentage,
      mergedCompetences,
      hoursSaved,
      totalCompetences1: competences1.length,
      totalCompetences2: competences2.length,
      sameRncp: false
    };
  };

  // Extraire les compétences textuelles depuis les compétences métier (codes ROME)
  const extractCompetencesFromBlocs = (competencesMetier, rncpCode, formationTitle) => {
    const competences = [];

    // Format: compétences depuis les codes ROME (API France Travail)
    competencesMetier?.forEach(romeData => {
      if (romeData.competences && Array.isArray(romeData.competences)) {
        // Extraire chaque compétence du code ROME
        romeData.competences.forEach(comp => {
          // comp peut être un string ou un objet
          const libelle = typeof comp === 'string' ? comp : (comp.libelle || comp.libelleCompetence || comp.label || '');
          if (libelle && libelle.length > 10) {
            competences.push({
              text: libelle,
              rncpCode: rncpCode,
              romeCode: romeData.codeRome,
              romeLibelle: romeData.libelle,
              formationTitle: formationTitle
            });
          }
        });
      }
    });

    return competences;
  };

  // Calculer la similarité textuelle (Jaccard similarity - AMÉLIORÉ comme /rncp-visualization)
  const calculateTextSimilarity = (text1, text2) => {
    if (!text1 || !text2) return 0;

    const normalize = (text) => {
      return text.toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Retirer les accents
        .replace(/[^\w\s]/g, ' ')         // Retirer ponctuation
        .replace(/\s+/g, ' ')             // Normaliser espaces
        .trim();
    };

    // Filtrer les mots trop courts (< 3 caractères) comme dans /rncp-visualization
    const words1 = new Set(normalize(text1).split(/\s+/).filter(w => w.length > 2));
    const words2 = new Set(normalize(text2).split(/\s+/).filter(w => w.length > 2));

    // Intersection et union
    const intersection = new Set([...words1].filter(x => words2.has(x)));
    const union = new Set([...words1, ...words2]);

    if (union.size === 0) return 0;
    return Math.round((intersection.size / union.size) * 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3">
            <Sparkles className="w-10 h-10 text-purple-600" />
            Analyseur de Fusion de Compétences
          </h1>
          <p className="text-lg text-gray-600">
            Découvrez les opportunités d'économie en fusionnant vos formations
          </p>
        </div>

        {/* Étape 1: Recherche SIRET */}
        {!organism && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-blue-600" />
              Étape 1: Rechercher votre organisme
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro SIRET
                </label>
                <input
                  type="text"
                  value={siret}
                  onChange={(e) => setSiret(formatSiret(e.target.value))}
                  placeholder="123 456 789 01234"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Le SIRET doit contenir exactement 14 chiffres
                </p>
              </div>

              <button
                onClick={handleSearchSiret}
                disabled={loading || !siret}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Recherche en cours...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    Rechercher l'organisme
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Étape 2: Validation organisme */}
        {organism && !formations.length && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Organisme trouvé
            </h2>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold text-blue-900 mb-2">{organism.name}</h3>
              <p className="text-blue-700">SIRET: {formatSiret(organism.siret)}</p>
              {organism.address && <p className="text-blue-700">{organism.address}</p>}
              {organism.totalFormations && (
                <p className="text-blue-700 mt-2">
                  {organism.totalFormations} formation{organism.totalFormations > 1 ? 's' : ''} disponible{organism.totalFormations > 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* Sélecteur de seuil de fusion */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Seuil de similarité pour la fusion de compétences
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="40"
                  max="100"
                  step="5"
                  value={fusionThreshold}
                  onChange={(e) => setFusionThreshold(Number(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-blue-600">{fusionThreshold}%</span>
                </div>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>40% (plus de résultats)</span>
                <span>100% (très strict)</span>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                💡 Plus le seuil est bas, plus vous trouverez de compétences similaires entre formations
              </p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setOrganism(null)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Modifier
              </button>
              <button
                onClick={handleValidateOrganism}
                disabled={loading}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center justify-center gap-2 transition-colors"
              >
                {loading ? 'Chargement...' : 'Valider et analyser les formations'}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Étape 3: Résultats de l'analyse */}
        {fusionAnalysis && (
          <div>
            {/* Top 2 formations avec fusion */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Award className="w-7 h-7 text-green-600" />
                  Meilleure opportunité de fusion
                </h2>
                <div className="flex items-center gap-3">
                  {/* Sélecteur de seuil rapide */}
                  <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
                    <label className="text-sm font-medium text-gray-700">Seuil:</label>
                    <select
                      value={fusionThreshold}
                      onChange={(e) => {
                        const newThreshold = Number(e.target.value);
                        setFusionThreshold(newThreshold);
                        analyzeFormationsFusion(formations, newThreshold);
                      }}
                      className="bg-white border border-gray-300 rounded px-2 py-1 text-sm font-semibold text-blue-600"
                    >
                      <option value="40">40%</option>
                      <option value="45">45%</option>
                      <option value="50">50%</option>
                      <option value="55">55%</option>
                      <option value="60">60%</option>
                      <option value="65">65%</option>
                      <option value="70">70%</option>
                      <option value="75">75%</option>
                      <option value="80">80%</option>
                      <option value="85">85%</option>
                      <option value="90">90%</option>
                      <option value="95">95%</option>
                      <option value="100">100%</option>
                    </select>
                  </div>
                  <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold">
                    {fusionAnalysis.topPair.similarity}% de similarité
                  </div>
                </div>
              </div>

              {/* Analyse détaillée */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-6 mb-6">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Formation 1 */}
                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Formation 1</h3>
                    <p className="text-sm text-gray-700 mb-2">{fusionAnalysis.topPair.formation1.title}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>📚 {fusionAnalysis.topPair.formation1.totalBlocs} blocs</span>
                      <span>✓ {fusionAnalysis.topPair.formation1.totalCompetences} compétences</span>
                    </div>
                  </div>

                  {/* Formation 2 */}
                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Formation 2</h3>
                    <p className="text-sm text-gray-700 mb-2">{fusionAnalysis.topPair.formation2.title}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>📚 {fusionAnalysis.topPair.formation2.totalBlocs} blocs</span>
                      <span>✓ {fusionAnalysis.topPair.formation2.totalCompetences} compétences</span>
                    </div>
                  </div>
                </div>

                {/* Compétences fusionnées */}
                <div className="bg-white rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    {fusionAnalysis.topPair.mergedCompetences.length} compétences peuvent être fusionnées
                  </h4>

                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {fusionAnalysis.topPair.mergedCompetences.map((merge, index) => (
                      <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-green-700">Similarité: {merge.similarity}%</span>
                        </div>
                        {/* Compétence formation 1 */}
                        <div className="mb-2">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
                              {merge.competence1?.rncpCode || 'N/A'}
                            </span>
                            <span className="text-xs text-gray-500">Formation 1</span>
                          </div>
                          <p className="text-sm text-gray-700">• {merge.competence1?.text || merge.competence1}</p>
                        </div>
                        {/* Compétence formation 2 */}
                        {merge.competence2 && (
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded">
                                {merge.competence2?.rncpCode || 'N/A'}
                              </span>
                              <span className="text-xs text-gray-500">Formation 2</span>
                            </div>
                            <p className="text-sm text-gray-600">• {merge.competence2?.text || merge.competence2}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Message marketing */}
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 rounded-xl p-6 text-center">
                <h3 className="text-2xl font-bold text-purple-900 mb-3">
                  🎉 Économisez jusqu'à {fusionAnalysis.topPair.hoursSaved} heures de formation !
                </h3>
                <p className="text-purple-800 text-lg mb-1">
                  En fusionnant ces compétences communes, vous optimisez vos parcours
                </p>
                <p className="text-purple-700">
                  et réduisez les redondances pour vos équipes pédagogiques
                </p>
              </div>
            </div>

            {/* Autres formations (blurred) */}
            {formations.length > 2 && (
              <div className="bg-white rounded-2xl shadow-xl p-8 relative">
                <div className={!showAllFormations ? 'filter blur-sm' : ''}>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Autres formations analysées ({formations.length - 2})
                  </h2>

                  <div className="grid md:grid-cols-2 gap-4">
                    {formations.filter(f =>
                      f.rncpCode !== fusionAnalysis.topPair.formation1.rncpCode &&
                      f.rncpCode !== fusionAnalysis.topPair.formation2.rncpCode
                    ).map((formation, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <p className="font-medium text-gray-900 mb-2">{formation.title}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>📚 {formation.totalBlocs} blocs</span>
                          <span>✓ {formation.totalCompetences} compétences</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Overlay pour débloquer */}
                {!showAllFormations && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/50 rounded-2xl">
                    <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md text-center">
                      <Lock className="w-16 h-16 text-purple-600 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        Débloquez l'analyse complète
                      </h3>
                      <p className="text-gray-600 mb-6">
                        Analysez toutes les combinaisons possibles et découvrez toutes les opportunités d'optimisation
                      </p>
                      <button
                        onClick={() => navigate('/pricing')}
                        className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 font-semibold text-lg transition-all transform hover:scale-105"
                      >
                        Analyser toutes les formations
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Loading spinner */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FusionTest;
